<template>
  <nav
    class="navigation-dropdown"
    :class="dropdownClass"
    ref="dropdown"
    data-test="navi-dropdown"
  >
    <FocusTrap :active="navigationOpen">
      <div class="navigation-container" @click="handleNavigationClick($event)">
        <ul class="navigation__list navigation__list--level-1">
          <li class="navigation__item navigation__item--level-1">
            <router-link
              :to="{ name: RouteNames.DASHBOARD }"
              class="navigation__link navigation__link--level-1"
              data-test="navi-link-dashboard"
              >{{ $t("navigation.home") }}</router-link
            >
          </li>
        </ul>
        <ul
          class="navigation__list navigation__list--level-1"
          v-if="reports.length > 0"
          data-test="navi-section-reports"
        >
          <li class="navigation__item navigation__item--level-1">
            <span class="navigation__header navigation__header--level-1">{{
              $t("navigation.reports")
            }}</span>
            <ul class="navigation__list navigation__list--level-2">
              <li
                v-for="{ key, title, routeName } in reports"
                :key="title"
                class="navigation__item navigation__item--level-2"
              >
                <span class="navigation__icon navigation__icon--level-2"
                  ><SVGIcon iconName="arrow-right" alt="" />
                </span>
                <router-link
                  :to="{ name: routeName }"
                  class="navigation__link navigation__link--level-2"
                  :data-test="'navi-link-report-' + key"
                  >{{ title }}</router-link
                >
              </li>
            </ul>
          </li>
        </ul>
        <ul
          class="navigation__list navigation__list--level-1"
          v-if="inventoryManagementPages.length > 0"
          data-test="navi-section-ims"
        >
          <li class="navigation__item navigation__item--level-1">
            <span class="navigation__header navigation__header--level-1">{{
              $t("navigation.inventoryManagement")
            }}</span>
            <ul class="navigation__list navigation__list--level-2">
              <li
                v-for="page in inventoryManagementPages"
                :key="page"
                class="navigation__item navigation__item--level-2"
              >
                <span class="navigation__icon navigation__icon--level-2"
                  ><SVGIcon iconName="arrow-right" alt="" />
                </span>
                <router-link
                  :to="`/ims/${page}`"
                  class="navigation__link navigation__link--level-2"
                  :data-test="'navi-link-ims-' + page"
                  >{{ $t(`inventoryManagementPages.${page}`) }}</router-link
                >
              </li>
            </ul>
          </li>
        </ul>
        <ul
          class="navigation__list navigation__list--level-1"
          v-if="servicesPages.length > 0"
          data-test="navi-section-debtorCreditor"
        >
          <li class="navigation__item navigation__item--level-1">
            <span class="navigation__header navigation__header--level-1">
              {{ $t("services.title") }}
            </span>
            <ul class="navigation__list navigation__list--level-2">
              <li
                v-for="{ key, title, routeName } in servicesPages"
                :key="title"
                class="navigation__item navigation__item--level-2"
              >
                <span class="navigation__icon navigation__icon--level-2"
                  ><SVGIcon iconName="arrow-right" alt="" />
                </span>
                <router-link
                  :to="{ name: routeName }"
                  class="navigation__link navigation__link--level-2"
                  :data-test="'navi-link-services-' + key"
                >
                  {{ $t(key) }}
                </router-link>
              </li>
            </ul>
          </li>
        </ul>
        <ul
          class="navigation__list navigation__list--level-1"
          v-if="quotationToolPages.length > 0"
          data-test="navi-section-chargeWeightCalculator"
        >
          <li class="navigation__item navigation__item--level-1">
            <span class="navigation__header navigation__header--level-1">
              {{ $t("navigation.tools") }}
            </span>
            <ul class="navigation__list navigation__list--level-2">
              <li
                v-for="{ key, routeName } in quotationToolPages"
                :key="key"
                class="navigation__item navigation__item--level-2"
              >
                <span class="navigation__icon navigation__icon--level-2"
                  ><SVGIcon iconName="arrow-right" alt="" />
                </span>
                <router-link
                  :to="{ name: routeName }"
                  class="navigation__link navigation__link--level-2"
                  :data-test="'quotation-tool-' + key"
                >
                  {{ $t(`quotationTool.${key}`) }}
                </router-link>
              </li>
            </ul>
          </li>
        </ul>
        <ul
          v-if="Config.isCompass"
          class="navigation__list navigation__list--level-1"
        >
          <li class="navigation__item navigation__item--level-1">
            <span class="navigation__header navigation__header--level-1">
              {{ $t("navigation.maps") }}</span
            >
            <ul class="navigation__list navigation__list--level-2">
              <li class="navigation__item navigation__item--level-2">
                <span class="navigation__icon navigation__icon--level-2"
                  ><SVGIcon iconName="arrow-right" alt="" />
                </span>
                <router-link
                  :to="{ name: RouteNames.TRUCK_DISPOSITION_MAP }"
                  class="navigation__link navigation__link--level-2"
                >
                  {{ $t(`location.truckDisposition`) }}
                </router-link>
              </li>
            </ul>
          </li>
        </ul>
      </div>
    </FocusTrap>
  </nav>
  <div
    class="navigation__cover"
    :class="{ 'navigation__cover--hidden': !navigationOpen }"
    @click="setNavigationOpen(false)"
  ></div>
</template>

<script setup lang="ts">
import { computed, type Ref, ref, watch } from "vue";
import { RouteNames } from "@/router";
import { useNavigationState } from "@/components/header/composables/navigationState";
import { usePermissions } from "@/common/permission";
import { useAuthStore } from "@/stores/auth";
import { FocusTrap } from "focus-trap-vue";
import { SVGIcon } from "et-components";
import { useGeneralStore } from "@/stores/general";
import { useI18n } from "vue-i18n";
import Config from "@/common/config";

const { t } = useI18n();

const { navigationOpen, setNavigationOpen } = useNavigationState();

const { permissionPresent, permissionsReady, hasPermissionUsageQueryAccess } =
  usePermissions();

type NavigationPage = {
  key: string;
  title: string;
  routeName: RouteNames;
};

const reports: Ref<NavigationPage[]> = ref([]);

const inventoryManagementPages: Ref<Array<string>> = ref([]);
const servicesPages: Ref<NavigationPage[]> = ref([]);

const quotationToolPages: Ref<Array<{ key: string; routeName: RouteNames }>> =
  ref([]);

const authStore = useAuthStore();

const generalStore = useGeneralStore();

const fullscreen = computed(() => {
  return generalStore.fullscreen;
});

const QUOTE_TOOL_COMPANY_PERMISSIONS = ["TUS010010", "TUS010011"];

const quoteToolCompanyPermissionPresent = () =>
  authStore.currentUser?.compassInformation?.companyPermissions?.some(
    ({ permission }) => QUOTE_TOOL_COMPANY_PERMISSIONS.includes(permission),
  ) ?? false;

watch(
  () => [permissionsReady, authStore.currentUser, fullscreen] as const,
  ([permsReady, _]) => {
    reports.value = [];
    inventoryManagementPages.value = [];
    quotationToolPages.value = [];
    servicesPages.value = [];

    if (permsReady.value) {
      if (permissionPresent("REPORT_ITR")) {
        reports.value.push({
          key: "itr",
          title: t("reports.itr"),
          routeName: RouteNames.REPORT_ITR,
        });
      }
      if (permissionPresent("REPORT_OOR")) {
        reports.value.push({
          key: "oor",
          title: t("reports.oor"),
          routeName: RouteNames.REPORT_OOR,
        });
      }
      if (permissionPresent("REPORT_WIR")) {
        reports.value.push({
          key: "wir",
          title: t("reports.wir"),
          routeName: RouteNames.REPORT_WIR,
        });
      }

      if (permissionPresent("REPORT_WIR_IM_VIEW")) {
        inventoryManagementPages.value.push(
          "deliveryIncoming",
          "deliveryOutgoing",
          "deliveryReceived",
          "deliverySent",
          "inventoryIms",
        );
      }
      if (Config.isCompass) {
        if (quoteToolCompanyPermissionPresent()) {
          quotationToolPages.value.push({
            key: "quoteTool",
            routeName: RouteNames.QUOTE_TOOL,
          });
        }
        servicesPages.value.push({
          key: "services.address",
          routeName: RouteNames.ADDRESS,
          title: t("services.address"),
        });
        servicesPages.value.push({
          key: "services.debtorCreditor",
          routeName: RouteNames.CUSTOMER_ADDRESS,
          title: t("services.debtorCreditor"),
        });
        if (hasPermissionUsageQueryAccess.value) {
          servicesPages.value.push({
            key: "services.permissionUsageQuery",
            routeName: RouteNames.PERMISSION_USAGE_QUERY,
            title: t("services.permissionUsageQuery"),
          });
        }
      }
      const quotationPagesToAdd = [
        // If not Compass and QUOTE_VIEW permission is present, add "newOffer"
        ...(Config.isCompass
          ? [{ key: "translation", routeName: RouteNames.TRANSLATION_TOOL }]
          : permissionPresent("QUOTE_VIEW")
            ? [{ key: "newOffer", routeName: RouteNames.NEW_OFFER }]
            : []),

        // Always add "calculator" page
        { key: "calculator", routeName: RouteNames.CHARGE_WEIGHT_CALCULATOR },
      ];
      quotationToolPages.value.push(...quotationPagesToAdd);
    }
  },
  { immediate: true },
);

const handleNavigationClick = (event: Event) => {
  // The click listener is registered on the navigation container but the events bubble up,
  // so clicks on children also trigger the listener.
  // Check if an actual navigation link has been clicked before emitting the close request.
  if (
    event.target !== null &&
    (event.target as HTMLElement).classList.contains("navigation__link")
  ) {
    setNavigationOpen(false);
  }
};

const dropdownClass = computed(
  () => `navigation-dropdown--${navigationOpen.value ? "open" : "closed"}`,
);

const onEscapeCloseNavigation = (ev: KeyboardEvent) => {
  if (ev.key == "Escape") {
    if (navigationOpen) {
      setNavigationOpen(false);
    }
  }
};

watch(navigationOpen, (opened) => {
  if (opened) {
    window.addEventListener("keydown", onEscapeCloseNavigation);
  } else {
    window.removeEventListener("keydown", onEscapeCloseNavigation);
  }
});
</script>

<style scoped lang="scss">
// TODO: extract to global styles and use variables instead of magic values

.navigation-dropdown {
  overflow: auto;
  background: white;

  @media screen and (max-width: $breakpoint-medium) {
    min-height: calc(100vh - #{$layout-header-height});
  }

  &--closed {
    display: none;
  }
}

.navigation__cover {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
}

.navigation__cover--hidden {
  display: none;
}

.navigation-container {
  max-width: $layout-max-content-width;

  margin: 3rem auto;

  display: flex;
  flex-flow: row wrap;
  gap: 8rem;

  @media screen and (max-width: $breakpoint-content-width) {
    margin: 3rem $layout-min-horizontal-margin;
  }
  @media screen and (max-width: $breakpoint-medium) {
    margin: 3rem 8rem;

    flex-flow: column nowrap;
    gap: 4rem;

    height: calc(100vh - 80px - 2 * 3rem);
  }
}

.navigation__list {
  display: block;
  list-style: none;
  margin: 0;
  padding: 0;
}

.navigation__list--level-2 {
  margin-top: 2.5rem;
}

.navigation__link {
  text-decoration: none;
}

%level-1 {
  color: $color-etBlue;
  font-size: $font-size-h3;
  font-weight: 700;
}

.navigation__header--level-1 {
  @extend %level-1;
  cursor: default;
}

.navigation__link--level-1 {
  @extend %level-1;

  &:hover {
    color: $color-etRed;
  }

  cursor: pointer;
}

.navigation__icon--level-2 {
  height: 1rem;
  width: 1rem;

  > svg {
    height: 100%;
    width: 100%;
    stroke: $color-etBlue;
  }
}

.navigation__item--level-2 {
  margin: 0.4rem 0;
  display: flex;
  align-items: center;
}

.navigation__icon--level-2 {
  margin-right: 1rem;
}

.navigation__link--level-2 {
  color: $color-etBlue;
  font-weight: bold;

  // use border instead of text-decoration for underlining
  // because text-decoration-thickness is not supported in all browsers
  border-bottom: 0.2rem solid transparent;

  &:hover {
    border-bottom: 0.2rem solid currentColor;
  }
}
</style>
